import { Extension } from "@tiptap/react";
import {
  CMS_FONT_SIZE_MAX_PX,
  CMS_FONT_SIZE_MIN_PX,
} from "@/lib/cms/cms-theme.constants";

/**
 * Normalise une `font-size` relue depuis le DOM vers une valeur `Npx` dans la
 * plage autorisée. Le navigateur sérialise toujours `element.style.fontSize`
 * en px. Aligné sur le sanitizer API (toute taille px bornée, pas seulement les
 * presets du menu).
 */
export function parseStoredFontSize(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  const match = raw.trim().match(/^(\d+(?:\.\d+)?)\s*px$/i);
  if (!match) return null;
  const px = Math.round(Number(match[1]));
  if (
    !Number.isFinite(px) ||
    px < CMS_FONT_SIZE_MIN_PX ||
    px > CMS_FONT_SIZE_MAX_PX
  ) {
    return null;
  }
  return `${px}px`;
}

/** Normalise une saisie utilisateur (`12`, `12px`, …) vers `Npx` ou null. */
export function normalizeFontSizeInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:px)?$/i);
  if (!match) return null;
  return parseStoredFontSize(`${Math.round(Number(match[1]))}px`);
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    cmsFontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

/**
 * Taille de police inline sur la sélection (`span[style=font-size]` via `textStyle`).
 * Même mécanisme que `CmsFontColor` : attribut global sur `textStyle`.
 * Presets du thème + saisie libre bornée (cf. CMS_FONT_SIZE_*_PX).
 */
export const CmsFontSize = Extension.create({
  name: "cmsFontSize",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => parseStoredFontSize(element.style.fontSize),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          const normalized = normalizeFontSizeInput(fontSize);
          if (!normalized) return false;
          return chain().setMark("textStyle", { fontSize: normalized }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: null }).run(),
    };
  },
});
